import { defaultLocale } from '../../util/locales/available-locales';
import { Locale } from '../../util/locales/locale.model';
import { getContentfulService } from '../http/http.factory';
import { Asset } from './static-assets-api.model';
import {
  AppNewsCollection,
  PromotionalBannerEntry,
  PromotionalBannerEntryResponse,
  Link,
  LinksCollectionRequest,
  LinksCollectionResponse,
  WarrantyExtensionEntry,
  MappingEntry,
  ContentfulCollection,
  AppNewsCollectionItem,
  SubsidiaryResponse,
} from './static-content-api.model';
import { GlobalLink, NavigationScope, NavItem, ProfileMenuLinkScope, Section } from './static-content.static-generation.model';

const getPreviewValue = (): boolean => {
  if (typeof window !== 'undefined') {
    const localStoragePreview = localStorage.getItem('CONTENTFUL_PREVIEW');
    if (localStoragePreview !== null) {
      return localStoragePreview === 'true';
    }
  }
  return process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true';
};

const preview = getPreviewValue();
export class StaticContentApiService {
  static getFooterLinksFetcher(
    locale: Locale,
    scope: string
  ): {
    key: string;
    fetcher: () => Promise<Section[]>;
  } {
    const { key, fetcher } = this.getLinksCollectionFetcher(locale, scope);
    return {
      key,
      fetcher: async () => {
        return (await fetcher()).map(StaticContentApiService.mapToSection);
      },
    };
  }

  static getFooterLegalEntityFetcher(locale: Locale, tags?: string[]) {
    return {
      key: 'getLegalEntity',
      fetcher: () => this.getLegalEntity(locale.RFC5646, tags),
    };
  }

  static getAppNewsFetcher(locale: Locale, tags?: string[]) {
    return {
      key: 'getAppNews',
      fetcher: () => this.getAppNews(locale.RFC5646, tags),
    };
  }

  static getNavigationLinksFetcher(
    locale: Locale,
    scope?: NavigationScope
  ): {
    key: string | null;
    fetcher: () => Promise<NavItem[]>;
  } {
    if (!scope) {
      return {
        key: null,
        fetcher: async () => {
          return (await fetcher()).map(StaticContentApiService.mapToNavItem);
        },
      };
    }
    const { key, fetcher } = this.getLinksCollectionFetcher(locale, scope);
    return {
      key,
      fetcher: async () => {
        return (await fetcher()).map(StaticContentApiService.mapToNavItem);
      },
    };
  }

  static getProfileLinksFetcher(
    locale: Locale,
    scope: ProfileMenuLinkScope
  ): {
    key: string;
    fetcher: () => Promise<GlobalLink[]>;
  } {
    const { key, fetcher } = this.getLinksCollectionFetcher(locale, scope);
    return {
      key,
      fetcher: async () => {
        return (await fetcher()).map(StaticContentApiService.mapToGlobalLink);
      },
    };
  }

  static getWarrantyExtensionEntryFetcher(locale: Locale, id: string) {
    return {
      key: 'getWarrantyExtension',
      fetcher: () => this.getWarrantyExtensionEntry(locale.RFC5646, id),
    };
  }

  static getPromotionalBannerEntryFetcher(locale: Locale, id: string) {
    return {
      key: `getPromotionalBanner${id}`,
      fetcher: () => this.getPromotionalBannerEntry(locale.RFC5646, id),
    };
  }

  static getSubsidiaryEntryFetcher(id: string, locale = defaultLocale) {
    return {
      key: `getSubsidiary`,
      fetcher: () => this.getSubsidiaryEntry(id, locale),
    };
  }

  static getMappingEntriesFetcher(scope: string, tags?: string[]) {
    return {
      key: `getMappingEntries${scope}`,
      fetcher: () => this.getMappingEntries(scope, tags),
    };
  }

  private static async getLegalEntity(locale: string, tags = ['imprintConnected']): Promise<string | undefined> {
    const body = {
      query: `query ($locale: String!, $preview: Boolean, $tags: [String!]) {
                        imprintCollection(
                          preview: $preview,
                          limit: 1,
                          locale: $locale,
                          where: { contentfulMetadata: { tags: { id_contains_some: $tags } } }
                    ) {
                      items {
                          provider
                      }
                }
            }`,
      variables: {
        locale,
        preview,
        tags,
      },
    };

    const response: ContentfulCollection<'imprintCollection', { provider: string }[]> = await getContentfulService().post(
      `/spaces/${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE}/environments/${process.env.NEXT_PUBLIC_CONTENTFUL_ENV}`,
      JSON.stringify(body)
    );

    return response?.data?.imprintCollection?.items[0]?.provider;
  }

  private static async getAppNews(locale: string, tags): Promise<AppNewsCollection> {
    const body = {
      query: `query ($locale: String!, $preview: Boolean, $tags: [String!]) {
                        stihlAppNewsCollection(
                          preview: $preview,
                          limit: 5,
                          locale: $locale,
                          order: publicationDate_DESC,
                          where: { header_exists: true, contentfulMetadata: { tags: { id_contains_some: $tags } } }
                    ) {
                      items {
                          header
                          contentText
                          link
                          imagesCollection(locale: "${defaultLocale.RFC5646}") {
                            items {
                              url
                              description
                            }
                          }
                      }
                }
            }`,
      variables: {
        locale,
        preview,
        tags,
      },
    };

    const response: ContentfulCollection<'stihlAppNewsCollection', AppNewsCollectionItem[]> = await getContentfulService().post(
      `/spaces/${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE}/environments/${process.env.NEXT_PUBLIC_CONTENTFUL_ENV}`,
      JSON.stringify(body)
    );

    return response?.data?.stihlAppNewsCollection?.items.map(this.mapAppNewsImageUrl);
  }

  private static mapAppNewsImageUrl(item: { imagesCollection: { items: Asset[] } } & AppNewsCollectionItem): AppNewsCollectionItem {
    return { ...item, image: item.imagesCollection.items?.find(({ url }) => !!url) ?? null };
  }

  private static async getWarrantyExtensionEntry(locale: string, id: string): Promise<WarrantyExtensionEntry> {
    const body = {
      query: `query ($locale: String!, $preview: Boolean, $id: String) {
                warrantyExtensionCollection(preview: $preview, locale: $locale, where: {id: $id}) {
                  items {
                    generalWarrantyConditionsUrl
                    importantInformation {
                      json
                    }
                    subsidiary {
                      ...SubsidiaryFields
                    }
                  }
                }
              }

              fragment SubsidiaryFields on StihlSubsidiary {
                fullLegalName
              }
            `,
      variables: {
        locale,
        preview,
        id,
      },
    };

    const response: ContentfulCollection<'warrantyExtensionCollection', WarrantyExtensionEntry[]> = await getContentfulService().post(
      `/spaces/${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE}/environments/${process.env.NEXT_PUBLIC_CONTENTFUL_ENV}`,
      JSON.stringify(body)
    );

    return response.data.warrantyExtensionCollection.items[0];
  }

  private static async getMappingEntries(scope: string, tags?: Array<string>): Promise<MappingEntry[]> {
    const body = {
      query: `query MappingEntries($scope: String!, $preview: Boolean, $tags: [String]) {
                mappingEntryCollection(
                    preview: $preview
                    where: { scope: $scope, contentfulMetadata: { tags: { id_contains_some: $tags } } }
                ) {
                    items {
                        key
                        value
                    }
                }
            }
      `,
      variables: {
        scope,
        preview,
        tags,
      },
    };

    const response: ContentfulCollection<'mappingEntryCollection', MappingEntry[]> = await getContentfulService().post(
      `/spaces/${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE}/environments/${process.env.NEXT_PUBLIC_CONTENTFUL_ENV}`,
      JSON.stringify(body)
    );

    return response.data.mappingEntryCollection.items;
  }

  private static async getPromotionalBannerEntry(locale: string, id: string): Promise<PromotionalBannerEntry | Record<string, never>> {
    const body = {
      query: `query ($locale: String!, $preview: Boolean, $id: String) {
        # query locale banner
        banner: promotionalBannerCollection(
          preview: $preview
          locale: $locale
          where: { id: $id, headline_exists: true, description_exists: true }
        ) {
          items {
            id
            type
            headline
            description
            image {
              fileName
            }
            imagePosition
            primaryButtonText
            primaryButtonLinkout
            secondaryButtonText
            secondaryButtonLinkout
          }
        }
        # query banner default locale
        bannerDefaultLocale: promotionalBannerCollection(
          preview: $preview
          locale: "${defaultLocale.RFC5646}"
          where: { id: $id, headline_exists: true, description_exists: true, primaryButtonText_exists: true, primaryButtonLinkout_exists: true }
        ) {
          items {
            id
            type
            headline
            description
            image {
              fileName
            }
            imagePosition
            primaryButtonText
            primaryButtonLinkout
            secondaryButtonText
            secondaryButtonLinkout
            slot
          }
        }
      }
      `,
      variables: {
        locale,
        preview,
        id,
      },
    };

    const response: PromotionalBannerEntryResponse = await getContentfulService().post(
      `/spaces/${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE}/environments/${process.env.NEXT_PUBLIC_CONTENTFUL_ENV}`,
      JSON.stringify(body)
    );

    return this.getResolvedBanner(response) ?? {};
  }

  private static getSubsidiaryEntry = (id: string, locale: Locale) => {
    const body = {
      query: `query ($id: String!, $locale: String!, $preview: Boolean) {
        stihlSubsidiaryCollection(
          preview: $preview
          locale: $locale
          where: {id: $id}
        ) {
          items {
            id
            fullLegalName
            shortName
            publicTelephone
            publicTelephoneInfo {
              json
            }
            address {
              json
            }
            publicEmail
            websiteUrl
          }
        }
      }
      `,
      variables: {
        id,
        locale: locale.RFC5646,
        preview,
      },
    };

    return getContentfulService().post<SubsidiaryResponse, string>(
      `/spaces/${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE}/environments/${process.env.NEXT_PUBLIC_CONTENTFUL_ENV}`,
      JSON.stringify(body)
    );
  };

  static getLinksCollectionFetcher(locale: Locale, scope: string) {
    return {
      key: `getLinksCollection${scope}${locale}`,
      fetcher: async () => StaticContentApiService.getLinksCollectionByScope(locale.RFC5646, scope),
    };
  }

  static getLinkByIdFetcher(locale: Locale, id: string) {
    return {
      key: `getLinkByIdFetcher${id}${locale}`,
      fetcher: async () => {
        const link = await StaticContentApiService.getLinkById(locale.RFC5646, id);
        return link ? StaticContentApiService.mapToGlobalLink(link) : undefined;
      },
    };
  }

  static getLinksByIdsFetcher(ids: string[], locale?: Locale | null) {
    if (!locale) {
      return {
        key: null,
        fetcher: null,
      };
    }

    return {
      key: `getLinksByIdFetcher${ids.join()}${locale}`,
      fetcher: async () => {
        const links = await StaticContentApiService.getLinksByIds(locale.RFC5646, ids);
        return links.map((link) => StaticContentApiService.mapToGlobalLink(link));
      },
    };
  }

  private static async getLinksCollectionByScope(locale: string, scope: string): Promise<Link[]> {
    const body: LinksCollectionRequest = {
      query: `query ($preview: Boolean, $scope: String, $locale: String) {
                linksCollection(preview: $preview, where: {scope_contains_some: [$scope], parent_exists: false}, locale: $locale, order: sortOrder_ASC) {
                    items {
                        name
                        id
                        url
                        type
                        dynamicNavItemId
                        linkedFrom (allowedLocales: [$locale, "${defaultLocale.RFC5646}"]) {
                            linksCollection {
                                items {
                                  name
                                  url
                                  icon
                                  id
                                  type
                                  sortOrder
                                }
                            }
                        }
                    }
                }
            }
        `,
      variables: {
        scope,
        locale,
        preview,
      },
    };

    return StaticContentApiService.makeLinksCollectionsRequest(body);
  }

  private static async getLinkById(locale: string, id: string): Promise<Link | undefined> {
    const body: LinksCollectionRequest = {
      query: `query ($preview: Boolean, $id: String, $locale: String) {
                linksCollection(preview: $preview, where: {id: $id}, locale: $locale, order: sortOrder_ASC) {
                    items {
                        name
                        id
                        url
                    }
                }
            }
        `,
      variables: {
        id,
        locale,
        preview,
      },
    };

    const links = await StaticContentApiService.makeLinksCollectionsRequest(body);

    return links.length ? links[0] : undefined;
  }

  private static async getLinksByIds(locale: string, ids: string[]): Promise<Link[]> {
    const body: LinksCollectionRequest = {
      query: `query getLinksByIds ($preview: Boolean, $ids: [String], $locale: String) {
                linksCollection(preview: $preview, where: {id_in: $ids}, locale: $locale, order: sortOrder_ASC) {
                    items {
                        name
                        id
                        url
                        eventName
                    }
                }
            }
        `,
      variables: {
        ids,
        locale,
        preview,
      },
    };

    return StaticContentApiService.makeLinksCollectionsRequest(body);
  }

  private static async makeLinksCollectionsRequest(body: LinksCollectionRequest) {
    const linksCollectionResponse: LinksCollectionResponse = await getContentfulService().post<any, any>(
      `/spaces/{contentfulSpace}/environments/{contentfulEnv}`,
      body,
      {
        pathParams: {
          contentfulSpace: process.env.NEXT_PUBLIC_CONTENTFUL_SPACE || '',
          contentfulEnv: process.env.NEXT_PUBLIC_CONTENTFUL_ENV || '',
        },
      }
    );

    return linksCollectionResponse.data.linksCollection?.items || [];
  }

  static mapToNavItem({ linkedFrom, sortOrder, ...link }: Link): NavItem {
    return {
      ...link,
      url: link.url || '#',
      type: link.type || 'Link',
      children:
        linkedFrom?.linksCollection?.items
          ?.filter(Boolean)
          .sort(StaticContentApiService.sortChildren)
          .map(StaticContentApiService.mapToNavItem) || null,
    };
  }

  static mapToSection({ linkedFrom, sortOrder, ...link }: Link): Section {
    return {
      ...link,
      title: link.name,
      links:
        linkedFrom?.linksCollection?.items
          ?.filter(Boolean)
          .sort(StaticContentApiService.sortChildren)
          .map(StaticContentApiService.mapToGlobalLink) || null,
    };
  }

  static mapToGlobalLink({ linkedFrom, sortOrder, ...link }: Link): GlobalLink {
    return {
      ...link,
      url: link.url || '#',
    };
  }

  static sortChildren(a: Link, b: Link): number {
    if (a.sortOrder === b.sortOrder) return 0;
    return a.sortOrder && b.sortOrder && a.sortOrder > b.sortOrder ? 1 : -1;
  }

  static getResolvedBanner(response: PromotionalBannerEntryResponse): PromotionalBannerEntry | undefined {
    const { banner, bannerDefaultLocale } = response.data || {};

    const bannerEntry = banner?.items[0];
    const bannerDefaultLocaleEntry = bannerDefaultLocale?.items[0];

    if (bannerEntry?.id) {
      const { image, primaryButtonLinkout, secondaryButtonLinkout } = bannerEntry || {};
      return {
        ...bannerEntry,
        image: image?.fileName ? image : bannerDefaultLocaleEntry?.image,
        primaryButtonLinkout: primaryButtonLinkout || bannerDefaultLocaleEntry?.primaryButtonLinkout,
        secondaryButtonLinkout: secondaryButtonLinkout || bannerDefaultLocaleEntry?.secondaryButtonLinkout,
      };
    }

    return bannerDefaultLocaleEntry;
  }
}
